import { createContext, useContext, useEffect, useMemo, useState } from "react";
import {
  normalizePublicLanguage,
  PUBLIC_LANGUAGE_OPTIONS,
} from "@/lib/publicLanguage";

const LANGUAGE_STORAGE_KEY = "rsi_public_language";

const LanguageContext = createContext({
  language: "en",
  setLanguage: () => {},
  isEn: true,
  isZh: false,
  isBn: false,
  languageOptions: PUBLIC_LANGUAGE_OPTIONS,
});

function readInitialLanguage() {
  if (typeof window === "undefined") return "en";
  try {
    const stored = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
    return normalizePublicLanguage(stored);
  } catch {
    // Ignore storage access issues.
  }
  return "en";
}

export function LanguageProvider({ children }) {
  const [language, setLanguage] = useState(readInitialLanguage);

  const updateLanguage = (nextLanguage) => {
    setLanguage(normalizePublicLanguage(nextLanguage));
  };

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
    } catch {
      // Ignore storage access issues.
    }
  }, [language]);

  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.lang = language === "bn" ? "bn-BD" : language;
  }, [language]);

  const value = useMemo(
    () => ({
      language,
      setLanguage: updateLanguage,
      isEn: language === "en",
      isZh: language === "zh-CN",
      isBn: language === "bn",
      languageOptions: PUBLIC_LANGUAGE_OPTIONS,
    }),
    [language]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useSiteLanguage() {
  return useContext(LanguageContext);
}
